<template>
    <v-container :fluid="true">
        <v-row v-if="user">
            <v-col class="mx-auto" cols="8">
                <v-expansion-panels variant="popout">
                    <v-expansion-panel>
                        <v-expansion-panel-title class="text-h6 font-weight-bold">Update Profile
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-form v-model="formData.valid" class="mb-3" enctype="multipart/form-data"
                                    validate-on="blur"
                                    @submit.prevent="updateProfile">
                                <v-container>
                                    <v-row>
                                        <v-col cols="12">
                                            <UserAvatar :src="avatar" :first-name="formData['first_name'] || user?.first_name"
                                                        :last-name="formData['last_name'] || user?.last_name"
                                                        :email="user?.email" size="150"/>
                                            <v-btn flat icon size="small" @click="$refs.imageHandle.click()">
                                                <v-icon icon="mdi-pencil"></v-icon>
                                            </v-btn>
                                            <v-file-input ref="imageHandle" v-model="formData['image']" accept="image/*"
                                                          class="d-none"
                                                          @change="processAndSetImage"
                                            />
                                            <p class="font-weight-bold mt-2 me-3">{{ user.email }}</p>
                                            <p v-if="authUser?.company?.account_code" class="text-caption text-grey">
                                                Account #: {{ authUser.company.account_code }}
                                            </p>
                                        </v-col>
                                    </v-row>
                                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable
                                             close-label="Close Alert"
                                             density="compact" variant="tonal">
                                        <p v-for="error in errors">{{ error }}</p>
                                    </v-alert>
                                    <v-row>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['first_name']"
                                                          :rules="[vRules.required]" density="compact"
                                                          label="First Name" autofocus/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['middle_name']" density="compact"
                                                          label="Middle Name"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['last_name']"
                                                          :rules="[vRules.required]" density="compact"
                                                          label="Last Name"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete v-model="formData['timezone']" :items="timezones"
                                                            :rules="[vRules.required]" density="compact"
                                                            item-title="title"
                                                            item-value="value" label="Timezone">
                                            </v-autocomplete>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['contact_phone']" density="compact"
                                                          :rules="[vRules.phone]" label="Contact Phone"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['mobile_phone']" density="compact"
                                                          :rules="[vRules.phone]" label="Mobile Phone"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_1']" density="compact"
                                                          label="Address 1"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_2']" density="compact"
                                                          label="Address 2"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_district']" density="compact"
                                                          label="District"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_city']" density="compact"
                                                          label="City"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_state']" density="compact"
                                                          label="State"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_postalcode']" density="compact"
                                                          :rules="[vRules.postalCode]" label="Postal Code"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete v-model="formData['addr_country']" :items="countries"
                                                            density="compact" label="Country"/>
                                        </v-col>
                                    </v-row>
                                </v-container>

                                <v-card-actions class="px-4 d-flex justify-end">
                                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save
                                    </v-btn>
                                </v-card-actions>
                            </v-form>
                        </v-expansion-panel-text>
                    </v-expansion-panel>

                    <v-expansion-panel @group:selected="onPasswordPanelToggle">
                        <v-expansion-panel-title class="text-h6 font-weight-bold">Change Password
                        </v-expansion-panel-title>
                        <v-expansion-panel-text ref="passwordPanel">
                            <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                                     density="compact" variant="tonal">
                                <p v-for="error in errors">{{ error }}</p>
                            </v-alert>
                            <v-form v-model="passwordFormData.valid"
                                    @submit.prevent="updatePassword">
                                <v-card-text class="text-start">
                                    <v-text-field v-model="passwordFormData.current_password"
                                                  :rules="[vRules.required, vRules.minLength(8)]"
                                                  autocomplete="current-password" label="Current Password"
                                                  type="password"/>
                                    <v-text-field v-model="passwordFormData.password"
                                                  :rules="[vRules.required, vRules.minLength(8)]"
                                                  autocomplete="new-password" label="Password"
                                                  type="password"/>
                                    <v-text-field v-model="passwordFormData.password_confirmation"
                                                  :rules="[vRules.required, vRules.minLength(8), vRules.sameAs(passwordFormData.password, 'password')]"
                                                  autocomplete="new-password" label="Confirm Password"
                                                  type="password"/>

                                </v-card-text>
                                <v-card-actions class="d-flex justify-end">
                                    <v-btn :disabled="!passwordFormData.valid" color="primary" size="small"
                                           type="submit" variant="text">Save
                                    </v-btn>
                                </v-card-actions>
                            </v-form>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {nextTick, onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import timezones from "@/assets/js/timezones.json";
import countries from "@/assets/js/countries.json";
import {useSnackbarStore} from "@/stores/snackBarStore";
import UserAvatar from "@/components/UserAvatar.vue";

const {user: authUser} = useAuthStore();
const user = ref();
const avatar = ref();
const errors = ref([]);
const passwordPanel = ref(null);
const onPasswordPanelToggle = (e) => {
    if (e.value) {
        nextTick(() => {
            setTimeout(() => {
                const container = passwordPanel.value?.$el ?? passwordPanel.value;
                const input = container?.querySelector('input');
                if (input) input.focus();
            }, 300);
        });
    }
};
const {updateAuthUser} = useAuthStore();
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    valid: false,
    image: null,
});
const passwordFormData = reactive({
    valid: false,
    password: null,
    current_password: null,
    password_confirmation: null,
})

onMounted(() => getProfile())

// Center-crop the chosen image to a square and downscale it client-side. This
// makes large pictures work (the upload is always small) and gives a predictable
// avatar instead of an arbitrary crop of the original.
const resizeImageToSquare = (file, size = 512) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
        URL.revokeObjectURL(url);
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2;
        const sy = (img.height - side) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        canvas.toBlob(
            (blob) => blob ? resolve(new File([blob], 'avatar.jpg', {type: 'image/jpeg'})) : reject(new Error('encode failed')),
            'image/jpeg',
            0.9,
        );
    };
    img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('image load failed'));
    };
    img.src = url;
});

const processAndSetImage = async () => {
    const file = formData['image'];
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
        showSnackbar('Please choose an image file.', 'error');
        formData['image'] = null;
        return;
    }
    // Guard against pathologically large source files before decoding.
    if (file.size > 25 * 1024 * 1024) {
        showSnackbar('Image is too large. Please choose a file under 25MB.', 'error');
        formData['image'] = null;
        return;
    }
    try {
        const processed = await resizeImageToSquare(file);
        formData['image'] = processed;
        avatar.value = URL.createObjectURL(processed);
    } catch (e) {
        showSnackbar('Could not process that image. Please try another.', 'error');
        formData['image'] = null;
    }
};
const getProfile = () => {
    send('get', `users/profile`)
        .then((response) => {
            if (response.data.success) {
                user.value = response.data.data;
                avatar.value = user.value.avatar;
                Object.keys(response.data.data).map((key) => {
                    if (!['email', 'avatar'].includes(key)) {
                        formData[key] = response.data.data[key];
                    }
                })
            }
        })
        .catch(function (errorResponse) {
            console.log(errorResponse)
            showSnackbar(errorResponse.response?.data?.message ?? 'Something went wrong', 'error');
        })
}
const updateProfile = () => {
    if (formData.valid) {
        errors.value = [];
        send('post', `users/profile`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then((response) => {
                showSnackbar(response.data.message);
                if (response.data.success) {
                    updateAuthUser(response.data.data)
                }
            })
            .catch(function (errorResponse) {
                if (errorResponse.response.data && errorResponse.response.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                showSnackbar('Something went wrong', 'error');
                document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
                console.log(errorResponse)
            })
    }
}
const updatePassword = () => {
    if (passwordFormData.valid) {
        errors.value = [];
        send('put', `users/password`, passwordFormData)
            .then((response) => {
                showSnackbar(response.data.message);
                passwordFormData.password = null;
                passwordFormData.current_password = null;
                passwordFormData.password_confirmation = null;
            })
            .catch(function (errorResponse) {
                if (errorResponse.response.data && errorResponse.response.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                showSnackbar('Something went wrong', 'error');
                console.log(errorResponse)
            })
    }
}
</script>
